import {
  useEffect,
  useMemo,
  useState
} from "react";

import api from "../../api";

export default function ReservationList() {

  // =========================================================
  // ÉTAT
  // =========================================================

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("tous");
  const [sourceFilter, setSourceFilter] = useState("tous");

  const [pdfLoading, setPdfLoading] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 10;

  // =========================================================
  // CHARGER LES RÉSERVATIONS
  // =========================================================

  const loadReservations = async () => {

    try {

      setLoading(true);
      setError("");

      const response =
        await api.get("/sejours");

      setReservations(
        Array.isArray(response.data)
          ? response.data
          : []
      );

    } catch (err) {

      console.error(
        "ERREUR CHARGEMENT RÉSERVATIONS:",
        err
      );

      setError(
        err?.response?.data?.error ||
        "Impossible de charger les réservations."
      );

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {

    loadReservations();

  }, []);

  // =========================================================
  // FORMATAGE
  // =========================================================

  const formatDate = (value) => {

    if (!value) return "—";

    const parts =
      String(value).split("-");

    if (parts.length === 3) {

      return `${parts[2]}/${parts[1]}/${parts[0]}`;

    }

    return value;
  };

  const formatMoney = (value) => {

    return Number(value || 0).toLocaleString(
      "fr-FR",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );
  };

  const getClientName = (reservation) => {

    const name = [
      reservation.nom,
      reservation.post_nom,
      reservation.prenom
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return (
      name ||
      reservation.client_nom ||
      `Client #${reservation.client_id || "?"}`
    );
  };

  // =========================================================
  // STATUT
  // =========================================================

  const getEffectiveStatus = (reservation) => {

    /*
     * Une réservation créée directement dans Immalta
     * avec statut "reserve" est considérée comme confirmée.
     *
     * On ne modifie PAS la valeur venant de la base.
     * On adapte uniquement son affichage.
     */

    if (
      reservation.source === "direct" &&
      reservation.statut === "reserve"
    ) {
      return "confirme";
    }

    return reservation.statut;
  };

  const getStatutLabel = (reservation) => {

    const statut =
      getEffectiveStatus(reservation);

    const labels = {

      reserve: "Réservée",

      confirme: "Confirmée",

      en_cours: "En cours",

      termine: "Terminée",

      annule: "Annulée",

      no_show: "No-show",

      expire: "Expirée"

    };

    return (
      labels[statut] ||
      statut ||
      "—"
    );
  };

  const getStatutClass = (reservation) => {

    const statut =
      getEffectiveStatus(reservation);

    const classes = {

      reserve:
        "bg-amber-100 text-amber-700 border border-amber-200",

      confirme:
        "bg-emerald-100 text-emerald-700 border border-emerald-200",

      en_cours:
        "bg-blue-100 text-blue-700 border border-blue-200",

      termine:
        "bg-slate-100 text-slate-700 border border-slate-200",

      annule:
        "bg-red-100 text-red-700 border border-red-200",

      no_show:
        "bg-purple-100 text-purple-700 border border-purple-200",

      expire:
        "bg-gray-100 text-gray-700 border border-gray-200"

    };

    return (
      classes[statut] ||
      "bg-slate-100 text-slate-700 border border-slate-200"
    );
  };

  // =========================================================
  // SOURCE
  // =========================================================

  const getSourceLabel = (source) => {

    if (source === "public") {
      return "Site Immalta";
    }

    return "Interne";
  };

  const getSourceClass = (source) => {

    if (source === "public") {

      return `
        bg-indigo-50
        text-indigo-700
        border
        border-indigo-100
      `;

    }

    return `
      bg-slate-100
      text-slate-700
      border
      border-slate-200
    `;
  };

  // =========================================================
  // FILTRAGE
  // =========================================================

  const filteredReservations = useMemo(() => {

    const query =
      search
        .trim()
        .toLowerCase();

    return reservations.filter(
      (reservation) => {

        const client =
          getClientName(reservation)
            .toLowerCase();

        const appartement =
          String(
            reservation.appartement_numero || ""
          ).toLowerCase();

        const id =
          String(
            reservation.id || ""
          ).toLowerCase();

        const uid =
          String(
            reservation.uid || ""
          ).toLowerCase();

        const matchesSearch =
          !query ||
          client.includes(query) ||
          appartement.includes(query) ||
          id.includes(query) ||
          uid.includes(query);

        const matchesStatut =
          statutFilter === "tous" ||
          getEffectiveStatus(reservation) === statutFilter;

        const matchesSource =
          sourceFilter === "tous" ||
          reservation.source === sourceFilter;

        return (
          matchesSearch &&
          matchesStatut &&
          matchesSource
        );

      }
    );

  }, [
    reservations,
    search,
    statutFilter,
    sourceFilter
  ]);

  // =========================================================
  // STATISTIQUES
  // =========================================================

  const statistics = useMemo(() => {

    const total =
      reservations.length;

    const confirmees =
      reservations.filter(
        (reservation) =>
          getEffectiveStatus(reservation) === "confirme"
      ).length;

    const enCours =
      reservations.filter(
        (reservation) =>
          reservation.statut === "en_cours"
      ).length;

    const site =
      reservations.filter(
        (reservation) =>
          reservation.source === "public"
      ).length;

    const internes =
      reservations.filter(
        (reservation) =>
          reservation.source === "direct"
      ).length;

    return {
      total,
      confirmees,
      enCours,
      site,
      internes
    };

  }, [reservations]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredReservations.length /
        ITEMS_PER_PAGE
      )
    );

  const paginatedReservations =
    useMemo(() => {

      const start =
        (currentPage - 1) *
        ITEMS_PER_PAGE;

      return filteredReservations.slice(
        start,
        start + ITEMS_PER_PAGE
      );

    }, [
      filteredReservations,
      currentPage
    ]);

  useEffect(() => {

    setCurrentPage(1);

  }, [
    search,
    statutFilter,
    sourceFilter
  ]);

  useEffect(() => {

    if (currentPage > totalPages) {

      setCurrentPage(totalPages);

    }

  }, [
    currentPage,
    totalPages
  ]);

  const startItem =
    filteredReservations.length === 0
      ? 0
      : (
          (currentPage - 1) *
          ITEMS_PER_PAGE
        ) + 1;

  const endItem =
    Math.min(
      currentPage * ITEMS_PER_PAGE,
      filteredReservations.length
    );

  // =========================================================
  // CONFIRMATION RÉSERVATION SITE
  // =========================================================

  const confirmerReservation = async (
    reservation
  ) => {

    const client =
      getClientName(reservation);

    const ok =
      window.confirm(
        `Confirmer la réservation de ${client} pour l'appartement ${reservation.appartement_numero} ?`
      );

    if (!ok) return;

    try {

      await api.put(
        `/sejours/${reservation.id}/confirmer`
      );

      await loadReservations();

      alert(
        "Réservation confirmée. L'e-mail de confirmation a été envoyé au client."
      );

    } catch (err) {

      console.error(
        "ERREUR CONFIRMATION:",
        err
      );

      alert(
        err?.response?.data?.error ||
        "Impossible de confirmer la réservation."
      );

    }

  };

  // =========================================================
  // GÉNÉRER PDF
  // =========================================================

  const generatePdf = async (
    reservation,
    lang
  ) => {

    const key =
      `${reservation.id}-${lang}`;

    try {

      setPdfLoading(key);

      const response =
        await api.get(
          `/sejours/${reservation.id}/confirmation-pdf`,
          {
            params: {
              lang
            },
            responseType: "blob"
          }
        );

      const blob =
        new Blob(
          [response.data],
          {
            type: "application/pdf"
          }
        );

      const url =
        window.URL.createObjectURL(blob);

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );

      setTimeout(() => {

        window.URL.revokeObjectURL(url);

      }, 60000);

    } catch (err) {

      console.error(
        "ERREUR PDF CONFIRMATION:",
        err
      );

      alert(
        "Impossible de générer la confirmation PDF."
      );

    } finally {

      setPdfLoading(null);

    }

  };


  // =========================================================
  // 📄 FICHE DE SÉJOUR
  // =========================================================

  const generateFicheSejourPdf = async (
    reservation,
    lang
  ) => {

    const key =
      `fiche-${reservation.id}-${lang}`;

    try {

      setPdfLoading(key);

      const response = await api.post(
        `/sejours/${reservation.id}/fiche-sejour-pdf`,
        {
          provenance: "",
          destination: "",
          lang
        },
        {
          responseType: "blob"
        }
      );

      const blob = new Blob(
        [response.data],
        {
          type: "application/pdf"
        }
      );

      const url =
        window.URL.createObjectURL(blob);

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);

    } catch (err) {

      console.error(
        "ERREUR PDF FICHE DE SÉJOUR:",
        err
      );

      alert(
        "Impossible de générer la fiche de séjour PDF."
      );

    } finally {

      setPdfLoading(null);

    }
  };
    
  

  // =========================================================
  // PAGINATION — NUMÉROS
  // =========================================================

  const getPageNumbers = () => {

    const pages = [];

    if (totalPages <= 7) {

      for (
        let i = 1;
        i <= totalPages;
        i++
      ) {

        pages.push(i);

      }

      return pages;
    }

    pages.push(1);

    if (currentPage > 4) {
      pages.push("...");
    }

    const start =
      Math.max(
        2,
        currentPage - 1
      );

    const end =
      Math.min(
        totalPages - 1,
        currentPage + 1
      );

    for (
      let i = start;
      i <= end;
      i++
    ) {

      pages.push(i);

    }

    if (currentPage < totalPages - 3) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages;
  };

  // =========================================================
  // RENDU
  // =========================================================

  return (

    <div className="w-full">

      {/* ================================================== */}
      {/* EN-TÊTE */}
      {/* ================================================== */}

      <div className="
        flex
        flex-col
        lg:flex-row
        lg:items-center
        lg:justify-between
        gap-4
        mb-6
      ">

        <div>

          <div className="
            flex
            items-center
            gap-3
          ">

            <div className="
              w-11
              h-11
              rounded-2xl
              bg-slate-900
              text-white
              flex
              items-center
              justify-center
              text-lg
              shadow-sm
            ">
              📋
            </div>

            <div>

              <h1 className="
                text-2xl
                font-black
                text-slate-900
              ">
                Réservations
              </h1>

              <p className="
                text-sm
                text-slate-500
                mt-0.5
              ">
                Gestion et confirmations des réservations
              </p>

            </div>

          </div>

        </div>

        <button
          onClick={loadReservations}
          disabled={loading}
          className="
            px-4
            py-2.5
            rounded-xl
            bg-slate-900
            text-white
            hover:bg-slate-800
            transition
            disabled:opacity-50
            font-semibold
            text-sm
            shadow-sm
          "
        >

          {loading
            ? "Actualisation..."
            : "↻ Actualiser"}

        </button>

      </div>

      {/* ================================================== */}
      {/* STATISTIQUES */}
      {/* ================================================== */}

      <div className="
        grid
        grid-cols-2
        lg:grid-cols-5
        gap-3
        mb-6
      ">

        {/* TOTAL */}

        <div className="
          bg-white
          rounded-2xl
          border
          border-slate-100
          shadow-sm
          p-4
        ">

          <div className="
            text-xs
            font-semibold
            text-slate-400
            uppercase
            tracking-wide
          ">
            Total
          </div>

          <div className="
            text-2xl
            font-black
            text-slate-900
            mt-1
          ">
            {statistics.total}
          </div>

        </div>

        {/* CONFIRMÉES */}

        <div className="
          bg-white
          rounded-2xl
          border
          border-emerald-100
          shadow-sm
          p-4
        ">

          <div className="
            text-xs
            font-semibold
            text-emerald-600
            uppercase
            tracking-wide
          ">
            Confirmées
          </div>

          <div className="
            text-2xl
            font-black
            text-emerald-700
            mt-1
          ">
            {statistics.confirmees}
          </div>

        </div>

        {/* EN COURS */}

        <div className="
          bg-white
          rounded-2xl
          border
          border-blue-100
          shadow-sm
          p-4
        ">

          <div className="
            text-xs
            font-semibold
            text-blue-600
            uppercase
            tracking-wide
          ">
            En cours
          </div>

          <div className="
            text-2xl
            font-black
            text-blue-700
            mt-1
          ">
            {statistics.enCours}
          </div>

        </div>

        {/* SITE */}

        <div className="
          bg-white
          rounded-2xl
          border
          border-indigo-100
          shadow-sm
          p-4
        ">

          <div className="
            text-xs
            font-semibold
            text-indigo-600
            uppercase
            tracking-wide
          ">
            Site Immalta
          </div>

          <div className="
            text-2xl
            font-black
            text-indigo-700
            mt-1
          ">
            {statistics.site}
          </div>

        </div>

        {/* INTERNES */}

        <div className="
          bg-white
          rounded-2xl
          border
          border-slate-100
          shadow-sm
          p-4
        ">

          <div className="
            text-xs
            font-semibold
            text-slate-500
            uppercase
            tracking-wide
          ">
            Internes
          </div>

          <div className="
            text-2xl
            font-black
            text-slate-700
            mt-1
          ">
            {statistics.internes}
          </div>

        </div>

      </div>

      {/* ================================================== */}
      {/* FILTRES */}
      {/* ================================================== */}

      <div className="
        bg-white
        rounded-2xl
        border
        border-slate-100
        shadow-sm
        p-4
        mb-6
      ">

        <div className="
          grid
          grid-cols-1
          md:grid-cols-3
          gap-3
        ">

          {/* RECHERCHE */}

          <div className="relative">

            <input
              type="text"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              placeholder="Client, appartement, ID ou référence..."
              className="
                w-full
                border
                border-slate-200
                rounded-xl
                px-4
                py-3
                pr-10
                outline-none
                focus:ring-2
                focus:ring-slate-200
                focus:border-slate-300
                text-sm
              "
            />

            {search && (

              <button
                type="button"
                onClick={() => setSearch("")}
                className="
                  absolute
                  right-3
                  top-1/2
                  -translate-y-1/2
                  text-slate-400
                  hover:text-slate-700
                "
                title="Effacer"
              >
                ×
              </button>

            )}

          </div>

          {/* STATUT */}

          <select
            value={statutFilter}
            onChange={(e) =>
              setStatutFilter(e.target.value)
            }
            className="
              border
              border-slate-200
              rounded-xl
              px-4
              py-3
              bg-white
              outline-none
              focus:ring-2
              focus:ring-slate-200
              text-sm
            "
          >

            <option value="tous">
              Tous les statuts
            </option>

            <option value="reserve">
              Réservées
            </option>

            <option value="confirme">
              Confirmées
            </option>

            <option value="en_cours">
              En cours
            </option>

            <option value="termine">
              Terminées
            </option>

            <option value="annule">
              Annulées
            </option>

            <option value="no_show">
              No-show
            </option>

            <option value="expire">
              Expirées
            </option>

          </select>

          {/* SOURCE */}

          <select
            value={sourceFilter}
            onChange={(e) =>
              setSourceFilter(e.target.value)
            }
            className="
              border
              border-slate-200
              rounded-xl
              px-4
              py-3
              bg-white
              outline-none
              focus:ring-2
              focus:ring-slate-200
              text-sm
            "
          >

            <option value="tous">
              Toutes les sources
            </option>

            <option value="public">
              Site Immalta
            </option>

            <option value="direct">
              Interne
            </option>

          </select>

        </div>

      </div>

      {/* ================================================== */}
      {/* ERREUR */}
      {/* ================================================== */}

      {error && (

        <div className="
          mb-6
          rounded-xl
          bg-red-50
          border
          border-red-100
          text-red-700
          px-4
          py-3
          text-sm
        ">

          {error}

        </div>

      )}

      {/* ================================================== */}
      {/* CHARGEMENT */}
      {/* ================================================== */}

      {loading &&
        reservations.length === 0 && (

        <div className="
          bg-white
          rounded-2xl
          border
          border-slate-100
          p-10
          text-center
          text-slate-500
        ">

          Chargement des réservations...

        </div>

      )}

      {/* ================================================== */}
      {/* TABLEAU */}
      {/* ================================================== */}

      {!loading && !error && (

        <div className="
          bg-white
          rounded-2xl
          border
          border-slate-100
          shadow-sm
          overflow-hidden
        ">

          {/* ------------------------------------------------ */}
          {/* BARRE SUPÉRIEURE */}
          {/* ------------------------------------------------ */}

          <div className="
            px-5
            py-4
            border-b
            border-slate-100
            flex
            flex-col
            sm:flex-row
            sm:items-center
            sm:justify-between
            gap-2
          ">

            <div>

              <div className="
                font-bold
                text-slate-900
              ">
                Liste des réservations
              </div>

              <div className="
                text-xs
                text-slate-400
                mt-0.5
              ">
                {filteredReservations.length === 0
                  ? "Aucun résultat"
                  : `${startItem}–${endItem} sur ${filteredReservations.length}`
                }
              </div>

            </div>

            {(search ||
              statutFilter !== "tous" ||
              sourceFilter !== "tous") && (

              <button
                type="button"
                onClick={() => {

                  setSearch("");
                  setStatutFilter("tous");
                  setSourceFilter("tous");

                }}
                className="
                  text-xs
                  font-semibold
                  text-slate-500
                  hover:text-slate-900
                "
              >
                Réinitialiser les filtres
              </button>

            )}

          </div>

          <div className="overflow-x-auto">

            <table className="w-full text-sm">

              <thead className="bg-slate-50">

                <tr>

                  <th className="text-left px-4 py-4 font-bold text-slate-600">
                    Client
                  </th>

                  <th className="text-left px-4 py-4 font-bold text-slate-600">
                    Appartement
                  </th>

                  <th className="text-left px-4 py-4 font-bold text-slate-600">
                    Séjour
                  </th>

                  <th className="text-left px-4 py-4 font-bold text-slate-600">
                    Source
                  </th>

                  <th className="text-left px-4 py-4 font-bold text-slate-600">
                    Statut
                  </th>

                  <th className="text-right px-4 py-4 font-bold text-slate-600">
                    Total
                  </th>

                  <th
                    colSpan="2"
                    className="text-center px-4 py-3 font-bold text-slate-600"
                  >
                    Actions
                  </th>

                </tr>

                <tr className="border-t border-slate-100">

                  <th colSpan="6"></th>

                  <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">
                    Fiche de séjour
                  </th>

                  <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">
                    Confirmation de réservation
                  </th>

                </tr>

              </thead>

              <tbody>

                {paginatedReservations.map(
                  (reservation) => {

                    const client =
                      getClientName(
                        reservation
                      );

                    /*
                     * Une réservation du site avec statut reserve
                     * doit être confirmée par la réception.
                     */
                    const isConfirmable =
                      reservation.source === "public" &&
                      reservation.statut === "reserve";

                    /*
                     * PDF disponible pour :
                     * - réservation interne reserve
                     * - réservation confirmée
                     * - séjour en cours
                     */
                    const canGeneratePdf =
                      (
                        reservation.source === "direct" &&
                        reservation.statut === "reserve"
                      ) ||
                      [
                        "confirme",
                        "en_cours"
                      ].includes(
                        reservation.statut
                      );

                    return (

                      <tr
                        key={reservation.id}
                        className="
                          border-t
                          border-slate-100
                          hover:bg-slate-50
                          transition
                        "
                      >

                        {/* CLIENT */}

                        <td className="px-4 py-4">

                          <div className="
                            font-semibold
                            text-slate-900
                          ">
                            {client}
                          </div>

                          <div className="
                            text-xs
                            text-slate-400
                            mt-1
                          ">
                            {reservation.uid
                              ? reservation.uid
                              : `#${reservation.id || "—"}`
                            }
                          </div>

                        </td>

                        {/* APPARTEMENT */}

                        <td className="px-4 py-4">

                          <span className="
                            inline-flex
                            px-2.5
                            py-1
                            rounded-lg
                            bg-slate-100
                            text-slate-800
                            font-semibold
                          ">
                            {reservation.appartement_numero || "—"}
                          </span>

                        </td>

                        {/* SÉJOUR */}

                        <td className="
                          px-4
                          py-4
                          whitespace-nowrap
                        ">

                          <div className="
                            text-slate-800
                            font-medium
                          ">
                            {formatDate(
                              reservation.date_debut
                            )}
                          </div>

                          <div className="
                            text-xs
                            text-slate-400
                            mt-1
                          ">
                            →
                            {" "}
                            {formatDate(
                              reservation.date_fin_prevue
                            )}
                          </div>

                        </td>

                        {/* SOURCE */}

                        <td className="px-4 py-4">

                          <span className={`
                            inline-flex
                            px-2.5
                            py-1
                            rounded-full
                            text-xs
                            font-semibold
                            ${getSourceClass(
                              reservation.source
                            )}
                          `}>
                            {getSourceLabel(
                              reservation.source
                            )}
                          </span>

                        </td>

                        {/* STATUT */}

                        <td className="px-4 py-4">

                          <span className={`
                            inline-flex
                            px-2.5
                            py-1
                            rounded-full
                            text-xs
                            font-bold
                            ${getStatutClass(
                              reservation
                            )}
                          `}>
                            {getStatutLabel(
                              reservation
                            )}
                          </span>

                        </td>

                        {/* TOTAL */}

                        <td className="
                          px-4
                          py-4
                          text-right
                          whitespace-nowrap
                          font-semibold
                          text-slate-800
                        ">

                          {formatMoney(
                            reservation.total_sejour
                          )} $

                        </td>

                        {/* ACTIONS */}

                        {/* ========================================================= */}
                        {/* ACTIONS — FICHE DE SÉJOUR + CONFIRMATION */}
                        {/* ========================================================= */}

                        {/* =============================== */}
                        {/* 📄 FICHE DE SÉJOUR */}
                        {/* =============================== */}

                        <td className="px-3 py-4 align-top">

                          {reservation.statut === "en_cours" ? (

                            <div className="flex justify-center gap-1">

                              {["fr", "en", "es"].map((lang) => {

                                const key =
                                  `fiche-${reservation.id}-${lang}`;

                                return (
                                  <button
                                    key={lang}
                                    onClick={() =>
                                      generateFicheSejourPdf(
                                        reservation,
                                        lang
                                      )
                                    }
                                    disabled={
                                      pdfLoading === key
                                    }
                                    title={
                                      lang === "fr"
                                        ? "Fiche de séjour en français"
                                        : lang === "en"
                                          ? "Fiche de séjour en anglais"
                                          : "Fiche de séjour en espagnol"
                                    }
                                    className="
                                      px-2.5
                                      py-1.5
                                      rounded-lg
                                      border
                                      border-slate-200
                                      bg-white
                                      text-slate-700
                                      text-xs
                                      font-semibold
                                      hover:bg-slate-50
                                      disabled:opacity-50
                                    "
                                  >
                                    {pdfLoading === key
                                      ? "..."
                                      : lang.toUpperCase()}
                                  </button>
                                );

                              })}

                            </div>

                          ) : (

                            <span className="
                              block
                              text-center
                              text-xs
                              text-slate-300
                            ">
                              —
                            </span>

                          )}

                        </td>


                        {/* =============================== */}
                        {/* 📝 CONFIRMATION DE RÉSERVATION */}
                        {/* =============================== */}

                        <td className="px-3 py-4 align-top">

                          {/* CONFIRMER */}
                          {isConfirmable && (
                            <button
                              onClick={() =>
                                confirmerReservation(
                                  reservation
                                )
                              }
                              className="
                                mb-2
                                w-full
                                px-2
                                py-1.5
                                rounded-lg
                                bg-emerald-600
                                text-white
                                text-xs
                                font-semibold
                                hover:bg-emerald-700
                                transition
                              "
                            >
                              ✓ Confirmer
                            </button>
                          )}

                          {/* PDF CONFIRMATION */}
                          {canGeneratePdf ? (

                            <div className="
                              flex
                              justify-center
                              gap-1
                            ">

                              {/* FR */}
                              <button
                                onClick={() =>
                                  generatePdf(
                                    reservation,
                                    "fr"
                                  )
                                }
                                disabled={
                                  pdfLoading ===
                                  `${reservation.id}-fr`
                                }
                                title="Confirmation en français"
                                className="
                                  px-2.5
                                  py-1.5
                                  rounded-lg
                                  bg-slate-900
                                  text-white
                                  text-xs
                                  font-semibold
                                  hover:bg-slate-800
                                  disabled:opacity-50
                                "
                              >
                                {pdfLoading ===
                                `${reservation.id}-fr`
                                  ? "..."
                                  : "FR"}
                              </button>


                              {/* EN */}
                              <button
                                onClick={() =>
                                  generatePdf(
                                    reservation,
                                    "en"
                                  )
                                }
                                disabled={
                                  pdfLoading ===
                                  `${reservation.id}-en`
                                }
                                title="Confirmation in English"
                                className="
                                  px-2.5
                                  py-1.5
                                  rounded-lg
                                  border
                                  border-slate-200
                                  bg-white
                                  text-slate-700
                                  text-xs
                                  font-semibold
                                  hover:bg-slate-50
                                  disabled:opacity-50
                                "
                              >
                                {pdfLoading ===
                                `${reservation.id}-en`
                                  ? "..."
                                  : "EN"}
                              </button>


                              {/* ES */}
                              <button
                                onClick={() =>
                                  generatePdf(
                                    reservation,
                                    "es"
                                  )
                                }
                                disabled={
                                  pdfLoading ===
                                  `${reservation.id}-es`
                                }
                                title="Confirmación en español"
                                className="
                                  px-2.5
                                  py-1.5
                                  rounded-lg
                                  border
                                  border-slate-200
                                  bg-white
                                  text-slate-700
                                  text-xs
                                  font-semibold
                                  hover:bg-slate-50
                                  disabled:opacity-50
                                "
                              >
                                {pdfLoading ===
                                `${reservation.id}-es`
                                  ? "..."
                                  : "ES"}
                              </button>

                            </div>

                          ) : (

                            <span className="
                              block
                              text-center
                              text-xs
                              text-slate-300
                            ">
                              —
                            </span>

                          )}

                        </td>

                      </tr>

                    );

                  }
                )}

                {/* AUCUN RÉSULTAT */}

                {paginatedReservations.length === 0 && (

                  <tr>

                    <td
                      colSpan="7"
                      className="
                        px-4
                        py-14
                        text-center
                      "
                    >

                      <div className="
                        text-3xl
                        mb-2
                      ">
                        🔎
                      </div>

                      <div className="
                        font-semibold
                        text-slate-700
                      ">
                        Aucune réservation trouvée
                      </div>

                      <div className="
                        text-sm
                        text-slate-400
                        mt-1
                      ">
                        Essayez de modifier vos critères de recherche.
                      </div>

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

          {/* ================================================= */}
          {/* PAGINATION */}
          {/* ================================================= */}

          {filteredReservations.length > 0 && (

            <div className="
              border-t
              border-slate-100
              px-4
              py-4
              flex
              flex-col
              sm:flex-row
              sm:items-center
              sm:justify-between
              gap-4
            ">

              <div className="
                text-xs
                text-slate-500
              ">

                Affichage de{" "}
                <span className="
                  font-semibold
                  text-slate-700
                ">
                  {startItem}
                </span>
                {" "}à{" "}
                <span className="
                  font-semibold
                  text-slate-700
                ">
                  {endItem}
                </span>
                {" "}sur{" "}
                <span className="
                  font-semibold
                  text-slate-700
                ">
                  {filteredReservations.length}
                </span>
                {" "}réservation(s)

              </div>

              <div className="
                flex
                items-center
                gap-1
              ">

                {/* PREMIÈRE PAGE */}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(1)
                  }
                  disabled={currentPage === 1}
                  className="
                    w-9
                    h-9
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    text-slate-600
                    hover:bg-slate-50
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    text-sm
                  "
                  title="Première page"
                >
                  «
                </button>

                {/* PRÉCÉDENTE */}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(
                      (page) =>
                        Math.max(
                          1,
                          page - 1
                        )
                    )
                  }
                  disabled={currentPage === 1}
                  className="
                    w-9
                    h-9
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    text-slate-600
                    hover:bg-slate-50
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    text-sm
                  "
                  title="Page précédente"
                >
                  ‹
                </button>

                {/* NUMÉROS */}

                {getPageNumbers().map(
                  (page, index) => {

                    if (page === "...") {

                      return (

                        <span
                          key={`ellipsis-${index}`}
                          className="
                            w-9
                            h-9
                            flex
                            items-center
                            justify-center
                            text-slate-400
                            text-sm
                          "
                        >
                          …
                        </span>

                      );

                    }

                    const active =
                      page === currentPage;

                    return (

                      <button
                        key={page}
                        type="button"
                        onClick={() =>
                          setCurrentPage(
                            page
                          )
                        }
                        className={`
                          w-9
                          h-9
                          rounded-lg
                          text-sm
                          font-semibold
                          transition
                          ${
                            active
                              ? `
                                bg-slate-900
                                text-white
                                shadow-sm
                              `
                              : `
                                border
                                border-slate-200
                                bg-white
                                text-slate-600
                                hover:bg-slate-50
                              `
                          }
                        `}
                      >
                        {page}
                      </button>

                    );

                  }
                )}

                {/* SUIVANTE */}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(
                      (page) =>
                        Math.min(
                          totalPages,
                          page + 1
                        )
                    )
                  }
                  disabled={
                    currentPage === totalPages
                  }
                  className="
                    w-9
                    h-9
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    text-slate-600
                    hover:bg-slate-50
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    text-sm
                  "
                  title="Page suivante"
                >
                  ›
                </button>

                {/* DERNIÈRE PAGE */}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(
                      totalPages
                    )
                  }
                  disabled={
                    currentPage === totalPages
                  }
                  className="
                    w-9
                    h-9
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    text-slate-600
                    hover:bg-slate-50
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    text-sm
                  "
                  title="Dernière page"
                >
                  »
                </button>

              </div>

            </div>

          )}

        </div>

      )}

    </div>

  );

}